/**
 * AttachmentsPanel - Unified attachments component for any entity type
 * 
 * Wraps FileList + FileUploader for consistent attachment handling across modals.
 * 
 * Usage:
 *   <AttachmentsPanel 
 *     entityType="goal" 
 *     entityId={goal.goal_id}
 *     productId={productContext.product_id}
 *     canEdit={isEditing}
 *   />
 */
import React, { useState } from 'react';
import { Paperclip, Upload } from 'lucide-react';
import { FileUploader, FileList } from '../files';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';

const AttachmentsPanel = ({ 
  entityType,  // 'feature' | 'goal' | 'epic' | 'initiative'
  entityId, 
  productId,
  canEdit = true,
  colors: propColors,
  maxFiles = 20,
  maxSizeMB = 500,
  onCountChange
}) => {
  const { getThemeColors } = useSafeMode();
  const { user } = useAuth();
  const colors = propColors || getThemeColors();
  
  const [fileListKey, setFileListKey] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  if (!entityId) {
    return (
      <div className={cn("flex flex-col items-center justify-center h-full min-h-[300px]", colors.textMuted)}>
        <Upload className="w-12 h-12 mb-4 opacity-30" />
        <p>Save the item first to add attachments.</p>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col h-full min-h-[400px]" data-testid={`attachments-panel-${entityType}`}>
      {/* Header */}
      <div className="flex items-center gap-2 px-2 py-3 border-b border-gray-200 dark:border-gray-700">
        <Paperclip className="w-4 h-4 text-gray-500" />
        <span className={cn("font-medium", colors.textPrimary)}>Attachments</span>
      </div>
      
      {/* Scrollable File List */}
      <div className="flex-1 overflow-y-auto px-2 py-4">
        <FileList
          key={fileListKey}
          entityType={entityType}
          entityId={entityId}
          canDelete={canEdit}
          onFileDeleted={() => setFileListKey(k => k + 1)}
          onCountChange={onCountChange}
          variant="list"
        />
      </div>
      
      {/* Upload Zone - Fixed at Bottom */}
      {canEdit && (
        <div className={cn("border-t p-3 mt-auto", colors.borderPrimary)}>
          <FileUploader
            organizationId={user?.organization_id}
            productId={productId}
            entityType={entityType}
            entityId={entityId}
            onUploadingChange={setIsUploading}
            onFilesChange={() => setFileListKey(k => k + 1)}
            onUploadComplete={() => setFileListKey(k => k + 1)}
            maxFiles={maxFiles}
            maxSizeMB={maxSizeMB}
            disabled={!canEdit}
          />
          {isUploading && (
            <p className={cn("text-xs mt-2 text-center", colors.textMuted)}>
              Uploading... Please wait before closing.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default AttachmentsPanel;
